import { useCallback, useEffect, useRef, useState } from "react";
import { Database, ArrowsClockwise, Plugs, CloudSlash, Gauge } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { api } from "../context/AuthContext";

const MODULE_COLORS = {
  portale: "#45E0D0",
  contabilita: "#9C8FF0",
  tesoreria: "#F2A93B",
  produzione: "#34d399",
  risorice: "#E0616F",
  posta: "#60a5fa",
  paghe: "#D95FA6",
  sohelp: "#10B981",
  paqway: "#F43F5E",
};

const STATUS_STYLE = {
  attivo: { label: "Attivo", dot: "#34d399", text: "text-emerald-400" },
  errore: { label: "Non risponde", dot: "#f87171", text: "text-red-400" },
  non_configurato: { label: "Da collegare", dot: "rgba(255,255,255,0.25)", text: "text-white/35" },
};

const hhmm = (iso) => new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

const LatencyChart = ({ latency }) => {
  if (!latency || !latency.points || latency.points.length < 2) return null;
  return (
    <div className="mt-5" data-testid="db-latency-chart">
      <div className="flex items-center justify-between mb-2">
        <span className="text-white text-sm font-semibold">Latenza di risposta — ultime 24 ore</span>
        <span className="text-[10px] text-white/35">controllo automatico ogni 5 minuti</span>
      </div>
      <div style={{ width: "100%", height: 180 }}>
        <ResponsiveContainer>
          <LineChart data={latency.points} margin={{ top: 5, right: 10, bottom: 0, left: -18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
            <XAxis dataKey="t" tickFormatter={hhmm} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} minTickGap={40} stroke="rgba(255,255,255,0.15)" />
            <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} stroke="rgba(255,255,255,0.15)" unit=" ms" allowDecimals={false} />
            <Tooltip
              labelFormatter={(v) => hhmm(v)}
              formatter={(value, name) => [`${value} ms`, latency.labels?.[name] || name]}
              contentStyle={{ background: "var(--surface-raised)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: "rgba(255,255,255,0.6)" }}
            />
            <Legend formatter={(name) => <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>{latency.labels?.[name] || name}</span>} iconSize={8} />
            {latency.modules.map((m) => (
              <Line key={m} type="monotone" dataKey={m} stroke={MODULE_COLORS[m] || "#94a3b8"}
                strokeWidth={1.8} dot={false} connectNulls={false} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const dayLabel = (day) => new Date(`${day}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short" });

const fmtDateTime = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const fmtDurata = (min) => (min >= 60 ? `${Math.floor(min / 60)}h ${min % 60}min` : `${min} min`);

const IncidentHistory = ({ incidents }) => {
  if (!incidents || incidents.length === 0) return null;
  return (
    <div className="mt-5" data-testid="db-incidents-section">
      <span className="text-white text-sm font-semibold">Storico rallentamenti e cadute</span>
      <div className="mt-2 flex flex-col gap-1.5">
        {incidents.map((inc, i) => {
          const isDown = inc.type === "caduta";
          const Icon = isDown ? CloudSlash : Gauge;
          return (
            <div key={i} data-testid={`db-incident-row-${i}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-white/10 px-3 py-2"
              style={{ background: "var(--surface-raised)" }}>
              <Icon size={15} weight="duotone" className={isDown ? "text-red-400" : "text-amber-400"} />
              <span className="text-white text-xs font-medium">{inc.label}</span>
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDown ? "text-red-400" : "text-amber-400"}`}>
                {isDown ? "Caduta" : "Rallentamento"}
              </span>
              <span className="text-[11px] text-white/45">{fmtDateTime(inc.started_at)}</span>
              <span className={`text-[11px] font-mono ${inc.in_corso ? "text-red-300 animate-pulse" : "text-white/45"}`}>
                {inc.in_corso ? `in corso da ${fmtDurata(inc.durata_min)}` : `durata ${fmtDurata(inc.durata_min)}`}
              </span>
              {inc.details && <span className="text-[10.5px] text-white/30 truncate max-w-[280px]" title={inc.details}>{inc.details}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const barColor = (pct) => {
  if (pct == null) return "rgba(255,255,255,0.12)";
  if (pct >= 99) return "#34d399";
  if (pct >= 90) return "#F2A93B";
  return "#f87171";
};

const UptimeBars = ({ days, points, moduleKey }) => (
  <div className="flex items-end gap-[3px] mt-1" data-testid={`db-uptime-bars-${moduleKey}`}>
    {days.map((d, i) => {
      const pct = points?.[i]?.pct ?? null;
      return (
        <div key={d} className="flex flex-col items-center gap-0.5 flex-1" title={`${dayLabel(d)} ${d.slice(8)}/${d.slice(5, 7)}: ${pct == null ? "nessun dato" : `${pct}% attivo`}`}>
          <div className="w-full rounded-sm" style={{ height: pct == null ? 4 : Math.max(4, Math.round((pct / 100) * 18)), background: barColor(pct), transition: "height 0.3s ease" }} />
          <span className="text-[7.5px] leading-none text-white/25 uppercase">{dayLabel(d).slice(0, 2)}</span>
        </div>
      );
    })}
  </div>
);

export const EcosystemDbStatus = ({ className = "" }) => {
  const [data, setData] = useState(null);
  const [uptime, setUptime] = useState(null);
  const [latency, setLatency] = useState(null);
  const [incidents, setIncidents] = useState(null);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);

  const load = useCallback(async (manual = false) => {
    if (manual) setBusy(true);
    try {
      const [statusRes, uptimeRes, latencyRes, incidentsRes] = await Promise.all([
        api.get("/admin/maintenance/db-status"),
        api.get("/admin/maintenance/db-uptime"),
        api.get("/admin/maintenance/db-latency"),
        api.get("/admin/maintenance/db-incidents"),
      ]);
      if (mounted.current) {
        setData(statusRes.data);
        setUptime(uptimeRes.data);
        setLatency(latencyRes.data);
        setIncidents(incidentsRes.data.incidents);
      }
    } catch {
      /* mantiene l'ultimo stato noto */
    } finally {
      if (manual && mounted.current) setBusy(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    const id = setInterval(() => load(), 30000);
    return () => { mounted.current = false; clearInterval(id); };
  }, [load]);

  const checkedAt = data?.checked_at
    ? new Date(data.checked_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : null;

  return (
    <div className={`border border-surface-border rounded-lg bg-surface p-6 animate-fade-up ${className}`} data-testid="db-status-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2.5">
          <Database size={22} weight="duotone" className="text-cyan-400" />
          <h3 className="font-heading text-lg font-semibold tracking-tight text-white">Stato database ecosistema</h3>
        </div>
        <button
          data-testid="db-status-refresh-button"
          onClick={() => load(true)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 h-9 px-5 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 rounded-full font-semibold text-xs disabled:opacity-40 transition-colors"
        >
          <ArrowsClockwise size={13} className={busy ? "animate-spin" : ""} />
          {busy ? "Verifica..." : "Aggiorna"}
        </button>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Verifica in tempo reale che i database dei moduli PAQ rispondano: AreaPAQ con controllo diretto sul database,
        gli altri moduli attraverso la loro app (il database risponde tramite l'API del modulo). Disponibilità degli ultimi 7 giorni,
        aggiornamento automatico ogni 30 secondi; se un modulo smette di rispondere o diventa insolitamente lento V.I.K.I. invia una notifica push ai master
        {checkedAt && <span className="text-white/35"> — ultimo controllo: {checkedAt}</span>}.
      </p>
      {!data ? (
        <p className="text-xs text-white/35" data-testid="db-status-loading">Verifica dei database in corso...</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="db-status-list">
          {data.modules.map((m) => {
            const st = STATUS_STYLE[m.status] || STATUS_STYLE.non_configurato;
            const points = uptime?.history?.[m.key];
            return (
              <div
                key={m.key}
                data-testid={`db-status-item-${m.key}`}
                className="rounded-md border border-white/10 p-3 flex flex-col gap-1.5"
                style={{ background: "var(--surface-raised)" }}
                title={m.error || undefined}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={m.status === "attivo" ? "animate-pulse" : ""}
                    style={{ width: 8, height: 8, borderRadius: "50%", background: st.dot, flexShrink: 0 }}
                  />
                  <span className="text-white text-sm font-medium truncate">{m.label}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-[10.5px] font-semibold uppercase tracking-wider ${st.text}`}>{st.label}</span>
                  <span className="flex items-center gap-1.5">
                    {m.status === "attivo" && typeof m.latency_ms === "number" && (
                      <span className="text-[10px] text-white/40 font-mono">{m.latency_ms} ms</span>
                    )}
                    {m.via === "app" && m.status !== "non_configurato" && (
                      <span className="text-[9px] text-white/30 uppercase tracking-wider" title="Verificato attraverso l'app del modulo">via app</span>
                    )}
                    {m.status === "non_configurato" && <Plugs size={12} className="text-white/25" />}
                  </span>
                </div>
                {m.error && <span className="text-[10px] text-red-400/80 truncate">{m.error}</span>}
                {uptime?.days && m.status !== "non_configurato" && (
                  <UptimeBars days={uptime.days} points={points} moduleKey={m.key} />
                )}
              </div>
            );
          })}
        </div>
      )}
      <LatencyChart latency={latency} />
      <IncidentHistory incidents={incidents} />
    </div>
  );
};
